import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErrorState, LoadingState, Notice, Spinner } from "@/components/common/States";
import { InviteLinkBox } from "@/components/common/InviteLinkBox";
import { InvitationTable } from "@/components/common/InvitationTable";
import { api, errorMessage, fetcher } from "@/lib/api";

export const SchoolAdminInvites = ({ school, onChanged }) => {
  const qc = useQueryClient();
  const key = ["platform-invites", school.id];
  const q = useQuery({ queryKey: key, queryFn: () => fetcher(`/platform/schools/${school.id}/invitations`) });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);

  const invite = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setErr("Format email tidak valid.");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/platform/schools/${school.id}/invitations`, { email: email.trim() });
      setCreated({ token: data.token, email: data.invitation.email });
      setEmail("");
      qc.invalidateQueries({ queryKey: key });
      onChanged();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (inv, reason) => {
    await api.post(`/platform/invitations/${inv.id}/revoke`, { reason });
    toast.success("Undangan dicabut.");
    qc.invalidateQueries({ queryKey: key });
  };

  return (
    <section className="space-y-4" data-testid="school-admin-invites">
      <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><Mail className="h-5 w-5 text-[#3B82F6]" /> Undangan Admin Sekolah</h2>
      {school.status === "Deactivated" ? (
        <Notice tone="muted">Sekolah dinonaktifkan sehingga undangan baru tidak dapat dibuat.</Notice>
      ) : (
        <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2" noValidate>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email calon Admin Sekolah" aria-label="Email calon Admin Sekolah" data-testid="admin-invite-email-input" />
          <Button type="submit" disabled={busy} data-testid="admin-invite-submit-button">{busy && <Spinner />} Buat tautan undangan</Button>
        </form>
      )}
      {err && <Notice tone="danger" testId="admin-invite-error">{err}</Notice>}
      {created && <InviteLinkBox token={created.token} email={created.email} />}
      {q.isLoading ? <LoadingState rows={2} /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : <InvitationTable items={q.data} onRevoke={revoke} />}
    </section>
  );
};
